/**
 * Platform settings: the admin team, and your own password.
 *
 * The comp also shows module toggles and security switches (2FA, auto-expiry).
 * Those have no backend, so rather than render dead switches that look
 * functional, this ships the two things that are real and notes the rest.
 */
import { useState } from 'react';
import { Plus, ShieldCheck, UserCircle, Users } from '@phosphor-icons/react';
import { useAuth } from '../auth/AuthContext';
import {
  useAdmins,
  useChangePassword,
  useCreateAdmin,
  useUpdateAdmin,
} from '../queries/platform';
import { apiError } from '../lib/api';
import { formatDate } from '../lib/format';
import {
  Avatar,
  Badge,
  ErrorState,
  Field,
  LoadingBlock,
  Modal,
  PreviewNote,
  SubmitButton,
} from '../components/ui';

export default function Settings() {
  const { admin } = useAuth();
  const isOwner = admin?.role === 'owner';

  return (
    <div className="content-narrow stack animate-up" style={{ gap: 18 }}>
      <AdminTeam isOwner={isOwner} currentId={admin?._id} />
      <ChangePassword />

      <div className="card">
        <div className="card-head">
          <ShieldCheck size={17} color="var(--ac)" />
          <h2 className="card-title">Security &amp; modules</h2>
        </div>
        <div className="card-pad">
          <PreviewNote>
            The design includes per-tenant module toggles and platform security switches (enforced
            2FA, auto-expiring sessions). Neither exists in the API yet, so they are not rendered
            here — a switch that looks functional but changes nothing is worse than its absence.
            Session length is currently fixed at 12 hours for this console.
          </PreviewNote>
        </div>
      </div>
    </div>
  );
}

function AdminTeam({ isOwner, currentId }: { isOwner: boolean; currentId?: string }) {
  const { data, isPending, isError, error, refetch } = useAdmins();
  const update = useUpdateAdmin();
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState('');

  const toggle = async (id: string, status: 'active' | 'inactive') => {
    setActionError('');
    try {
      await update.mutateAsync({ id, status });
    } catch (err) {
      setActionError(apiError(err));
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <Users size={17} color="var(--ac)" />
        <div>
          <h2 className="card-title">Admin team</h2>
          <p className="card-sub">Who can review registrations and onboard agencies</p>
        </div>
        <div className="spacer" />
        {isOwner && (
          <button className="btn btn-sm" onClick={() => setAdding(true)}>
            <Plus size={14} weight="bold" /> Add teammate
          </button>
        )}
      </div>

      {isPending ? (
        <LoadingBlock />
      ) : isError ? (
        <ErrorState message={apiError(error)} onRetry={() => void refetch()} />
      ) : (
        <div>
          {actionError && (
            <p className="field-error" style={{ padding: '12px 20px 0' }}>
              {actionError}
            </p>
          )}
          {data.map((member) => (
            <div
              key={member._id}
              className="table-row"
              style={{ gridTemplateColumns: '1.6fr 1fr .8fr auto' }}
            >
              <div className="row" style={{ minWidth: 0 }}>
                <Avatar name={member.name} size={34} />
                <div style={{ minWidth: 0 }}>
                  <p className="truncate strong" style={{ margin: 0, fontSize: 13.5 }}>
                    {member.name}
                    {member._id === currentId && <span className="faint"> (you)</span>}
                  </p>
                  <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                    {member.email}
                  </p>
                </div>
              </div>

              <div>
                <Badge tone={member.role === 'owner' ? 'info' : 'off'}>
                  {member.role === 'owner' ? 'Owner' : 'Admin'}
                </Badge>
              </div>

              <div>
                <span className="t-sm muted">
                  {member.lastLoginAt ? formatDate(member.lastLoginAt) : 'Never signed in'}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {isOwner && member._id !== currentId ? (
                  <button
                    className={`btn btn-sm ${member.status === 'inactive' ? 'btn-ok' : 'btn-bad'}`}
                    onClick={() =>
                      toggle(member._id, member.status === 'inactive' ? 'active' : 'inactive')
                    }
                    disabled={update.isPending}
                  >
                    {member.status === 'inactive' ? 'Reactivate' : 'Deactivate'}
                  </button>
                ) : (
                  <Badge tone={member.status === 'inactive' ? 'bad' : 'ok'}>
                    {member.status === 'inactive' ? 'Inactive' : 'Active'}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && <AddAdminModal onClose={() => setAdding(false)} />}
    </div>
  );
}

function AddAdminModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAdmin();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin' as 'owner' | 'admin',
  });
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.email.trim()) return setError('Name and email are required');
    if (form.password.length < 10) return setError('Password must be at least 10 characters');
    try {
      await create.mutateAsync({ ...form, name: form.name.trim(), email: form.email.trim() });
      onClose();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <Modal
      title="Add a teammate"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <SubmitButton
            pending={create.isPending}
            pendingLabel="Adding…"
            className="btn btn-primary"
            onClick={submit}
          >
            Add teammate
          </SubmitButton>
        </>
      }
    >
      <div className="stack" style={{ gap: 12 }}>
        <Field label="Name" required>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Email" required>
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field
          label="Temporary password"
          hint="At least 10 characters. Share it out of band and have them change it."
          required
        >
          <input
            className="input"
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <Field label="Role" hint="Only an owner can manage the admin team.">
          <select
            className="select"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as 'owner' | 'admin' })}
          >
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </Field>
        {error && <p className="field-error">{error}</p>}
      </div>
    </Modal>
  );
}

function ChangePassword() {
  const change = useChangePassword();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDone(false);
    if (form.newPassword.length < 10) return setError('New password must be at least 10 characters');
    if (form.newPassword !== form.confirm) return setError('The two passwords do not match');
    try {
      await change.mutateAsync({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <UserCircle size={17} color="var(--ac)" />
        <h2 className="card-title">Your password</h2>
      </div>
      <form onSubmit={submit} className="card-pad stack" style={{ gap: 14, maxWidth: 420 }}>
        <Field label="Current password">
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
          />
        </Field>
        <Field label="New password" hint="At least 10 characters.">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
          />
        </Field>
        <Field label="Confirm new password">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          />
        </Field>
        {error && <p className="field-error">{error}</p>}
        {done && (
          <p className="t-sm" style={{ color: 'var(--ok)', margin: 0 }}>
            Password changed.
          </p>
        )}
        <SubmitButton
          type="submit"
          pending={change.isPending}
          pendingLabel="Saving…"
          className="btn btn-primary"
          style={{ alignSelf: 'flex-start' }}
        >
          Change password
        </SubmitButton>
      </form>
    </div>
  );
}
